'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useActionState, useEffect, useMemo, useState, useTransition } from 'react';
import { useFormStatus } from 'react-dom';
import { createDevToken, createStudentIfNotExists, createTestToken } from '~/app/actions/auth';
import { Course } from '~/payload-types';
import { SupportedLocales } from '~/types';
const JWT_STORAGE_KEY = 'jwt';
const JWT_UPDATED_EVENT = 'jwt:updated';

// In-memory fallback for sandboxed iframes where sessionStorage is blocked
let memoryJwt: string | null = null;
function getStoredJwt(): string | null {
  try {
    return sessionStorage.getItem(JWT_STORAGE_KEY);
  } catch {
    return memoryJwt;
  }
}
function persistJwt(jwt: string) {
  try {
    sessionStorage.setItem(JWT_STORAGE_KEY, jwt);
  } catch {
    memoryJwt = jwt;
  }
  window.dispatchEvent(new Event(JWT_UPDATED_EVENT));
}
export function useAuthToken() {
  const $ = _c(2);
  const [token, setToken] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const updateToken = () => setToken(getStoredJwt());
      updateToken();
      window.addEventListener(JWT_UPDATED_EVENT, updateToken);
      return () => window.removeEventListener(JWT_UPDATED_EVENT, updateToken);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return token;
}
export function useDevToken() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  if (process.env.NEXT_PUBLIC_NODE_ENV !== "development") {
    throw new Error("useDevToken can only be used in development");
  }
  createDevToken().then(_temp);
}
function _temp(token) {
  persistJwt(token);
}
export function useProdToken() {
  const $ = _c(11);
  const query = useSearchParams();
  let t0;
  if ($[0] !== query) {
    t0 = query.get("jwt");
    $[0] = query;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const jwt = t0;
  let t1;
  if ($[2] !== query) {
    t1 = query.get("test");
    $[2] = query;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const testMode = t1 === "true";
  const [, startTransition] = useTransition();
  let t2;
  if ($[4] !== jwt || $[5] !== startTransition || $[6] !== testMode) {
    t2 = () => {
      if (process.env.NEXT_PUBLIC_NODE_ENV !== "production") {
        throw new Error("useProdToken can only be used in production");
      }
      if (jwt) {
        startTransition(async () => {
          ;
          try {
            const newJwt = await createStudentIfNotExists({
              jwt
            });
            persistJwt(newJwt);
            const url = new URL(window.location.href);
            url.search = "";
            window.history.replaceState(null, "", url.toString());
          } catch (t3) {
            const error = t3;
            console.error("Failed to create student:", error);
          }
        });
      } else {
        if (testMode) {
          console.warn("\uD83E\uDDEA TEST MODE: Creating development token for testing purposes");
          startTransition(_temp3);
        }
      }
    };
    $[4] = jwt;
    $[5] = startTransition;
    $[6] = testMode;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== jwt || $[9] !== testMode) {
    t3 = [jwt, testMode];
    $[8] = jwt;
    $[9] = testMode;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
}
async function _temp3() {
  ;
  try {
    const testJwt = await createTestToken();
    persistJwt(testJwt);
    console.log("\u2705 Test token created and stored");
  } catch (t0) {
    const error_0 = t0;
    console.error("Failed to create test token:", error_0);
  }
}
export function useAuthenticatedFormAction(action) {
  const $ = _c(7);
  const token = useAuthToken();
  const {
    pending
  } = useFormStatus();
  let t0;
  if ($[0] !== action || $[1] !== token) {
    t0 = (t1, formData) => {
      if (token) {
        return action(formData, token);
      }
      return {
        message: "Please login to submit the form"
      };
    };
    $[0] = action;
    $[1] = token;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      message: ""
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [formState, formAction] = useActionState(t0, t1);
  let t2;
  if ($[4] !== formState || $[5] !== pending) {
    t2 = {
      pending,
      formState,
      formAction
    };
    $[4] = formState;
    $[5] = pending;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function useCourseTimes(t0) {
  const $ = _c(4);
  const {
    course
  } = t0;
  let t1;
  if ($[0] !== course.relatedModules?.docs) {
    t1 = course.relatedModules?.docs || [];
    $[0] = course.relatedModules?.docs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const relatedModules = t1;
  let t2;
  if ($[2] !== relatedModules) {
    t2 = relatedModules.reduce(_temp5, 0);
    $[2] = relatedModules;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const courseTimes = t2;
  return courseTimes;
}
function _temp5(acc_m, relation) {
  if (typeof relation === "number") {
    return acc_m;
  }
  const module_ = relation.module;
  if (typeof module_ === "number") {
    return acc_m;
  }
  const timeModule = module_.units?.reduce(_temp4, 0);
  return acc_m + (timeModule ?? 0);
}
function _temp4(acc_u, unit) {
  if (typeof unit === "number") {
    return acc_u;
  }
  return acc_u + (unit.time ?? 0);
}
export function useComposeNavigationLink({
  courseOlatEntry,
  nextOlatNode,
  locale,
  courseId,
  moduleId
}: {
  courseOlatEntry: string;
  nextOlatNode?: string | null;
  locale: SupportedLocales;
  courseId: string;
  moduleId?: string | null;
}) {
  if (!nextOlatNode) {
    return null;
  }
  if (process.env.NODE_ENV !== 'development') {
    return `https://lms.uzh.ch/url/RepositoryEntry/${courseOlatEntry}/CourseNode/${nextOlatNode}`;
  }
  let pathHref = `/${locale}/course/${courseId}`;
  if (moduleId) {
    pathHref += `/module/${moduleId}`;
  }
  return pathHref;
}
export function useOptimisticUpdate(initialState, updateFn) {
  const $ = _c(7);
  const [isPending, startTransition] = useTransition();
  const [persistedState, setPersistedState] = useState(initialState);
  const [optimisticState, setOptimisticState] = useState(persistedState);
  let t0;
  if ($[0] !== persistedState || $[1] !== updateFn) {
    t0 = function doOptimisticUpdate(newValue) {
      setOptimisticState(newValue);
      startTransition(async () => {
        ;
        try {
          await updateFn(newValue);
          setPersistedState(newValue);
        } catch (t1) {
          const e = t1;
          console.error(e);
          setOptimisticState(persistedState);
        }
      });
    };
    $[0] = persistedState;
    $[1] = updateFn;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const doOptimisticUpdate = t0;
  let t1;
  if ($[3] !== doOptimisticUpdate || $[4] !== isPending || $[5] !== optimisticState) {
    t1 = {
      isPending,
      optimisticState,
      doOptimisticUpdate
    };
    $[3] = doOptimisticUpdate;
    $[4] = isPending;
    $[5] = optimisticState;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}